import { useEffect, useState } from 'react'
import {
  Users,
  MessageSquare,
  ShieldCheck,
  Repeat,
  Wallet,
  Smartphone,
  Tablet,
  Monitor,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  TrendingUp,
  TrendingDown,
  KeyRound,
} from 'lucide-react'
import { apiFetch } from '../config/api'

// ============================================================================
// v2 summary types (mirror apps/api/src/routes/analytics-dashboard.ts)
// ============================================================================

interface FunnelStageV2 {
  stage: string
  event: string
  users: number
  fromPrevious: number
  dropOffUsers: number
  dropOffRate: number
  cumulative: number
  medianMsFromStart?: number | null
}

interface UserFunnelV2 {
  cohort: number
  stages: FunnelStageV2[]
}

interface ActivationV2 {
  periodStart: string
  periodEnd: string
  activatedUsers: number
  qualifiedActivatedUsers: number
  qualifiedRate: number
  activatedFromFlow: number
  activationRate: number
}

interface RetentionCohortV2 {
  activationWeek: string
  activatedUsers: number
  d1: { value: number | null; preliminary: boolean }
  d7: { value: number | null; preliminary: boolean }
  d30: { value: number | null; preliminary: boolean }
  d1Exact: number | null
  d7Exact: number | null
  d30Exact: number | null
  d1Rolling: number | null
  d7Rolling: number | null
  d30Rolling: number | null
}

interface AcquisitionRowV2 {
  source: string
  visitors: number
  activatedUsers: number
  activationRate: number
  referralClicks: number
}

interface AIReliabilityV2 {
  requests: number
  succeeded: number
  failed: number
  timeout: number
  successRate: number
  failureRate: number
  timeoutRate: number
  rateLimitEvents: number
  retryRate: number
  latencyMs: { p50: number | null; p90: number | null; p95: number | null }
  byErrorCategory: Record<string, number>
}

interface JourneySplitV2 {
  guest: { activatedUsers: number; firstMessageUsers: number }
  wallet: {
    activatedUsers: number
    firstMessageUsers: number
    authSessionUsers: number
  }
}

interface IdentityAdoptionV2 {
  didCreation: { started: number; succeeded: number; successRate: number }
  historySave: { started: number; succeeded: number; successRate: number }
  historyRetrieval: { started: number; succeeded: number; successRate: number }
}

interface EngagementV2 {
  messages: number
  aiResponses: number
  secondMessages: number
}

interface DataQualityV2 {
  acceptedToday: number
  duplicateToday: number
  rejectedToday: number
  unknownSourceRate: number
  funnelCoverage: Record<string, number>
  lastErrors: number
}

interface DashboardSummaryV2 {
  period: { start?: string; end?: string }
  weeks: number
  funnel: UserFunnelV2
  activation: ActivationV2
  retention: RetentionCohortV2[]
  acquisition: AcquisitionRowV2[]
  aiReliability: AIReliabilityV2
  journey: JourneySplitV2
  identity: IdentityAdoptionV2
  engagement: EngagementV2
  dataQuality: DataQualityV2
}

// ============================================================================
// Types (mirror the API response shapes from apps/api/src/routes/analytics-dashboard.ts)
// ============================================================================

interface FunnelMetrics {
  landing_visits: number
  wallet_clicks: number
  wallet_successes: number
  session_successes: number
  did_successes: number
  first_messages: number
  first_replies: number
  returns_24h: number
}

interface FunnelConversionRates {
  wallet_click_to_success: number
  wallet_success_to_session: number
  session_to_did: number
  did_to_first_message: number
  first_message_to_first_reply: number
  overall_landing_to_first_reply: number
  return_rate_24h: number
}

interface DailyMetrics {
  date: string
  landing_visits: number
  wallet_clicks: number
  wallet_successes: number
  session_successes: number
  did_successes: number
  first_messages: number
  first_replies: number
}

interface DeviceBreakdown {
  mobile: number
  tablet: number
  desktop: number
  unknown: number
}

interface SessionsPerVisitor {
  avgSessionsPerVisitor: number
  totalVisitors: number
  totalSessions: number
}

interface DropOffItem {
  stage: string
  count: number
  dropOffFromPrevious: number
  dropOffRate: number
}

interface DashboardSummary {
  funnel: {
    metrics: FunnelMetrics
    conversionRates: FunnelConversionRates
  }
  daily: DailyMetrics[]
  devices: DeviceBreakdown
  sessions: SessionsPerVisitor
  walletProviders: Record<string, number>
  dropoffs: DropOffItem[]
  period: { days: number; startDate: string; endDate: string }
}

const STAGES: { key: keyof FunnelMetrics; label: string }[] = [
  { key: 'landing_visits', label: 'Landing visits' },
  { key: 'wallet_clicks', label: 'Wallet clicks' },
  { key: 'wallet_successes', label: 'Wallet connected' },
  { key: 'session_successes', label: 'Sessions started' },
  { key: 'did_successes', label: 'DIDs minted' },
  { key: 'first_messages', label: 'First message sent' },
  { key: 'first_replies', label: 'First reply received' },
]

const STAGE_LABELS: Record<DropOffItem['stage'], string> = {
  landing_visit: 'Landing visits',
  wallet_click: 'Wallet clicks',
  wallet_success: 'Wallet connected',
  session_success: 'Sessions started',
  did_success: 'DIDs minted',
  first_message: 'First message sent',
  first_reply: 'First reply received',
}

// ============================================================================
// Helpers
// ============================================================================

function formatNumber(n: number): string {
  return n.toLocaleString('en-US')
}

function formatPct(fraction: number, digits = 0): string {
  return `${(fraction * 100).toFixed(digits)}%`
}

/** Max value across a funnel-stage series, used to keep funnel bar widths proportional. */
function maxOf(metrics: FunnelMetrics): number {
  return Math.max(...STAGES.map((s) => metrics[s.key]), 1)
}

function parseProvider(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value : 'unknown'
}

// ============================================================================
// Component
// ============================================================================

export default function AnalyticsDashboard() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState<DashboardSummary | null>(null)
  const [dataV2, setDataV2] = useState<DashboardSummaryV2 | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [unlocked, setUnlocked] = useState(false)
  const [secretInput, setSecretInput] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [secretError, setSecretError] = useState<string | null>(null)
  const [signingIn, setSigningIn] = useState(false)
  const [stale, setStale] = useState(false)

  useEffect(() => {
    if (!unlocked) return
    let cancelled = false
    setError(null)
    setStale(false)

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 15000)

    ;(async () => {
      try {
        const [res, resV2] = await Promise.all([
          apiFetch(`/analytics/dashboard/summary?days=${days}`, {
            headers: { 'X-Admin-Secret': secretInput },
            signal: controller.signal,
          }),
          apiFetch(`/analytics/dashboard/v2/summary?days=${days}&weeks=8`, {
            headers: { 'X-Admin-Secret': secretInput },
            signal: controller.signal,
          }),
        ])
        if (cancelled) return
        if (!res.ok) {
          const body = await res.json().catch(() => null)
          setError(body?.message ?? `Request failed (${res.status})`)
          setStale(true)
          return
        }
        const [json, jsonV2] = await Promise.all([res.json(), resV2.json()])
        if (cancelled) return
        setData(json.data)
        if (resV2.ok && jsonV2?.data) {
          setDataV2(jsonV2.data)
        } else {
          // Legacy-only deploy: v2 panels show "unavailable" rather than fabricating zeros.
          setDataV2(null)
        }
      } catch (err) {
        if (cancelled) return
        setError('Failed to load analytics. Check that the API is running and ADMIN_SECRET is configured.')
        setStale(true)
      } finally {
        if (!cancelled) {
          setStale(false)
        }
      }
    })()

    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [days, unlocked, secretInput])

  const handleUnlock = async () => {
    if (!secretInput.trim()) {
      setSecretError('Enter the admin secret to continue.')
      return
    }
    setSigningIn(true)
    setSecretError(null)
    try {
      const res = await apiFetch('/analytics/dashboard/summary?days=30', {
        headers: { 'X-Admin-Secret': secretInput },
      })
      if (res.ok) {
        // Badge gates the data immediately and persists in this tab.
        setUnlocked(true)
      } else {
        let detail = ''
        try {
          const body = await res.json()
          detail = body?.error ?? body?.message ?? ''
        } catch {
          // non-JSON body (e.g. 502 Caddy error page, Express 404)
        }
        const reason = detail ? ` (${detail})` : ''
        const reqUrl = res.url || (typeof window !== 'undefined' ? `${window.location.origin}/api/analytics/dashboard/summary` : '')
        setSecretError(`Unlock failed: HTTP ${res.status}${reason} from ${reqUrl}. Check the API logs / ADMIN_SECRET.`)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      setSecretError(`Could not reach the analytics API (${msg || 'network error'}). Is the API server running?`)
    } finally {
      setSigningIn(false)
    }
  }

  // ── Lock screen ────────────────────────────────────────────────
  if (!unlocked) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0F1115] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-8">
            <div className="w-12 h-12 rounded-xl bg-primary-600/10 dark:bg-primary-500/10 flex items-center justify-center mb-6">
              <Lock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-xl font-semibold text-heading dark:text-white">Analytics dashboard</h1>
            <p className="text-sm text-body dark:text-gray-400 mt-2 mb-6">
              Admin-protected view of the onboarding funnel. Enter the API admin secret to continue.
            </p>

            {secretError && (
              <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-300">
                {secretError}
              </div>
            )}

            <label className="block text-xs font-medium text-body dark:text-gray-400 mb-1.5">
              Admin secret
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <KeyRound className="w-4 h-4 text-body dark:text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={secretInput}
                  onChange={(e) => setSecretInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleUnlock()
                  }}
                  autoFocus
                  placeholder="••••••••••••"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-heading dark:text-white placeholder:text-body/40 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-body dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button
              type="button"
              onClick={() => void handleUnlock()}
              disabled={signingIn}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60 transition-colors"
            >
              {signingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Unlock dashboard
            </button>
            <p className="text-xs text-body dark:text-gray-500 mt-4 text-center">
              Only shows aggregated, anonymized metrics. No personal data.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ── Error state (keeps the lock screen's backdrop) ─────────────
  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0F1115] flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center">
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
          <button
            type="button"
            onClick={() => setUnlocked(false)}
            className="mt-4 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
          >
            Back
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0F1115] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    )
  }

  const { funnel, daily, devices, sessions, walletProviders, dropoffs } = data

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0F1115] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-heading dark:text-white">Analytics</h1>
            <p className="text-sm text-body dark:text-gray-400 mt-1">
              Onboarding funnel · last {days} days
            </p>
          </div>
          <div className="flex items-center gap-2">
            {stale && (
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> stale
              </span>
            )}
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-heading dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {[7, 30, 90].map((d) => (
                <option key={d} value={d}>
                  Last {d} days
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setUnlocked(false)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-body dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Lock className="w-3.5 h-3.5" /> Lock
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard
            icon={<Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
            label="Unique visitors"
            value={formatNumber(sessions.totalVisitors)}
          />
          <KpiCard
            icon={<Repeat className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
            label="Sessions / visitor"
            value={sessions.avgSessionsPerVisitor.toFixed(2)}
          />
          <KpiCard
            icon={<MessageSquare className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
            label="First replies"
            value={formatNumber(funnel.metrics.first_replies)}
          />
          <KpiCard
            icon={<ShieldCheck className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
            label="24h return rate"
            value={formatPct(funnel.conversionRates.return_rate_24h)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Funnel */}
          <FunnelCard metrics={funnel.metrics} conversions={funnel.conversionRates} />
          {/* Device breakdown */}
          <DeviceCard devices={devices} />
          {/* Wallet providers */}
          <WalletCard providers={walletProviders} />
          {/* Daily trend */}
          <TrendCard daily={daily} />
          {/* Drop-offs */}
          <DropoffCard dropoffs={dropoffs} />
        </div>

        {/* ── v2 product analytics (launch KPIs) ─────────────────────────── */}
        {dataV2 ? (
          <div className="mt-10 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-heading dark:text-white">
                Product analytics (v2)
              </h2>
              <span className="text-[11px] text-body dark:text-gray-500">
                Unique-user, activation-cohort KPIs · {days}-day window
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <V2FunnelCard funnel={dataV2.funnel} />
              <V2ActivationCard activation={dataV2.activation} journey={dataV2.journey} />
              <V2AIReliabilityCard reliability={dataV2.aiReliability} />
            </div>

            <V2RetentionCard cohorts={dataV2.retention} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <V2AcquisitionCard rows={dataV2.acquisition} />
              <V2IdentityCard identity={dataV2.identity} engagement={dataV2.engagement} />
              <V2DataQualityCard dq={dataV2.dataQuality} />
            </div>
          </div>
        ) : (
          <div className="mt-10 p-5 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 text-sm text-body dark:text-gray-400">
            v2 product analytics (user-level funnel, activation cohorts, AI reliability) are
            unavailable — the API doesn't expose <code className="text-xs">/v2/summary</code> yet.
            This distinguishes "no data" from "tracking not deployed".
          </div>
        )}

        <div className="flex justify-end mt-8">
          <button
            type="button"
            onClick={() => setUnlocked(false)}
            className="inline-flex items-center gap-1.5 text-xs text-body dark:text-gray-500 hover:text-heading dark:hover:text-gray-300"
          >
            <Lock className="w-3.5 h-3.5" /> Lock dashboard
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Small building blocks
// ============================================================================

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-heading dark:text-white mb-4">{title}</h2>
      {children}
    </div>
  )
}

function KpiCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <span className="text-xs text-body dark:text-gray-400">{label}</span>
      </div>
      <p className="text-2xl font-semibold text-heading dark:text-white">{value}</p>
    </div>
  )
}

function FunnelCard({
  metrics,
  conversions,
}: {
  metrics: FunnelMetrics
  conversions: FunnelConversionRates
}) {
  const max = maxOf(metrics)
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 lg:col-span-2">
      <h2 className="text-sm font-semibold text-heading dark:text-white mb-4">Onboarding funnel</h2>
      <div className="space-y-3">
        {STAGES.map((stage, i) => {
          const count = metrics[stage.key]
          const width = Math.max((count / max) * 100, count > 0 ? 2 : 0)
          const prev = i > 0 ? metrics[STAGES[i - 1].key] : count
          const pctOfPrev = prev > 0 ? count / prev : 0
          return (
            <div key={stage.key}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-body dark:text-gray-400">{stage.label}</span>
                <span className="text-heading dark:text-white font-medium">
                  {formatNumber(count)}
                  {i > 0 && (
                    <span className="ml-2 text-body dark:text-gray-500">
                      {formatPct(pctOfPrev, 1)}
                    </span>
                  )}
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700/50 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-600 dark:bg-primary-500 transition-all"
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
      <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <ConversionRow label="Landing → reply" value={formatPct(conversions.overall_landing_to_first_reply, 1)} />
        <ConversionRow label="Wallet click → success" value={formatPct(conversions.wallet_click_to_success, 1)} />
        <ConversionRow label="Landing → session" value={formatPct(conversions.wallet_success_to_session * conversions.wallet_click_to_success, 1)} />
      </div>
    </div>
  )
}

function ConversionRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] text-body dark:text-gray-500">{label}</p>
      <p className="text-sm font-semibold text-heading dark:text-white mt-0.5">{value}</p>
    </div>
  )
}

function DeviceRow({ icon, label, count, total }: { icon: React.ReactNode; label: string; count: number; total: number }) {
  const pct = total > 0 ? count / total : 0
  return (
    <div>
      <div className="flex items-center gap-2 text-xs mb-1">
        {icon}
        <span className="text-body dark:text-gray-400 capitalize">{label}</span>
        <span className="ml-auto text-heading dark:text-white font-medium">{formatNumber(count)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700/50 overflow-hidden">
        <div className="h-full rounded-full bg-secondary-600 dark:bg-secondary-500" style={{ width: `${Math.max(pct * 100, count > 0 ? 2 : 0)}%` }} />
      </div>
    </div>
  )
}

function DeviceCard({ devices }: { devices: DeviceBreakdown }) {
  const total = devices.mobile + devices.tablet + devices.desktop + devices.unknown
  return (
    <Card title="Devices">
      <div className="space-y-3">
        <DeviceRow icon={<Monitor className="w-3.5 h-3.5 text-body dark:text-gray-500" />} label="desktop" count={devices.desktop} total={total} />
        <DeviceRow icon={<Smartphone className="w-3.5 h-3.5 text-body dark:text-gray-500" />} label="mobile" count={devices.mobile} total={total} />
        <DeviceRow icon={<Tablet className="w-3.5 h-3.5 text-body dark:text-gray-500" />} label="tablet" count={devices.tablet} total={total} />
      </div>
      <p className="text-[11px] text-body dark:text-gray-500 mt-4">{formatNumber(total)} landing events</p>
    </Card>
  )
}

function WalletCard({ providers }: { providers: Record<string, number> }) {
  const entries = Object.entries(providers)
  const total = entries.reduce((sum, [, count]) => sum + count, 0)
  const fallback = Object.keys(providers).length === 0
  return (
    <Card title="Wallet providers">
      {fallback ? (
        <p className="text-xs text-body dark:text-gray-500">No wallet events in this period.</p>
      ) : (
        <ul className="space-y-2">
          {entries.map(([name, count]) => (
            <li key={name} className="flex items-center gap-2 text-sm">
              <Wallet className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
              <span className="text-heading dark:text-white capitalize">{parseProvider(name)}</span>
              <span className="ml-auto text-body dark:text-gray-400">{formatNumber(count)}</span>
            </li>
          ))}
        </ul>
      )}
      {!fallback && <p className="text-[11px] text-body dark:text-gray-500 mt-4">{formatNumber(total)} wallet clicks</p>}
    </Card>
  )
}

interface TrendBar { date: string; label: string; value: number }

function TrendBarRow({ row, max }: { row: TrendBar; max: number }) {
  const width = max > 0 ? Math.max((row.value / max) * 100, row.value > 0 ? 3 : 0) : 0
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-16 shrink-0 text-body dark:text-gray-500">{row.label}</span>
      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700/50 overflow-hidden">
        <div className="h-full rounded-full bg-primary-600 dark:bg-primary-500" style={{ width: `${width}%` }} />
      </div>
      <span className="w-10 text-right text-heading dark:text-white font-medium">{row.value}</span>
    </div>
  )
}

function TrendCard({ daily }: { daily: DailyMetrics[] }) {
  const items = daily.length > 0 ? daily : [{ date: new Date().toISOString().split('T')[0], landing_visits: 0 } as DailyMetrics]
  const rows: TrendBar[] = items.map((d) => ({
    date: d.date,
    label: d.date.slice(5),
    value: d.landing_visits,
  }))
  const max = Math.max(...rows.map((r) => r.value), 1)
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-heading dark:text-white mb-4">Daily activity</h2>
      <div className="space-y-2">
        {rows.map((row) => (
          <TrendBarRow key={row.date} row={row} max={max} />
        ))}
      </div>
      <p className="text-[11px] text-body dark:text-gray-500 mt-4">Landing visits per day</p>
    </div>
  )
}

function DropoffCard({ dropoffs }: { dropoffs: DropOffItem[] }) {
  // Drop-offs are ordered (highest first, the far right of the funnel is the worst loss).
  const ordered = [...dropoffs].sort((a, b) => b.dropOffRate - a.dropOffRate)
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-heading dark:text-white mb-4">Biggest drop-offs</h2>
      <ul className="space-y-2">
        {ordered.map((drop) => (
          <li key={drop.stage} className="text-xs">
            <div className="flex items-center gap-2">
              {drop.dropOffRate > 0.6 ? (
                <TrendingDown className="w-3.5 h-3.5 text-red-500" />
              ) : drop.dropOffRate > 0 ? (
                <TrendingDown className="w-3.5 h-3.5 text-amber-500" />
              ) : (
                <TrendingUp className="w-3.5 h-3.5 text-green-600" />
              )}
              <span className="text-body dark:text-gray-400">{STAGE_LABELS[drop.stage] ?? drop.stage}</span>
              <span className="ml-auto font-medium text-heading dark:text-white">{formatPct(drop.dropOffRate, 0)}</span>
            </div>
            <p className="text-[11px] text-body dark:text-gray-500 mt-0.5 ml-[22px]">
              {formatNumber(drop.dropOffFromPrevious)} users dropped
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ============================================================================
// v2 product-analytics cards (launch KPIs)
// ============================================================================

const STAGE_LABELS_V2: Record<string, string> = {
  session_start: 'Session started',
  cta_click: 'CTA clicked',
  wallet_connect: 'Wallet connected',
  auth_session: 'Signed in',
  first_message: 'First message',
  activated: 'Activated (1st AI reply)',
  qualified_activation: 'Qualified (2nd msg)',
  dine: 'Median',
}

function V2FunnelCard({ funnel }: { funnel: UserFunnelV2 }) {
  const max = Math.max(...funnel.stages.map((s) => s.users), 1)
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 lg:col-span-1">
      <h2 className="text-sm font-semibold text-heading dark:text-white mb-1">Activation funnel (unique users)</h2>
      <p className="text-xs text-body dark:text-gray-400 mb-4">Cohort: {formatNumber(funnel.cohort)} session-started visitors</p>
      <div className="space-y-3">
        {funnel.stages.map((stage, i) => {
          const width = max > 0 ? Math.max((stage.users / max) * 100, stage.users > 0 ? 2 : 0) : 0
          const median = stage.medianMsFromStart != null ? `${Math.round(stage.medianMsFromStart / 60000)}m` : '—'
          return (
            <div key={stage.stage}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-body dark:text-gray-400">{STAGE_LABELS_V2[stage.stage] ?? stage.stage}</span>
                <span className="text-heading dark:text-white font-medium">
                  {formatNumber(stage.users)}
                  {i > 0 && (
                    <span className="ml-2 text-body dark:text-gray-500">{formatPct(stage.fromPrevious, 0)}</span>
                  )}
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700/50 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-600 dark:bg-primary-500 transition-all"
                  style={{ width: `${width}%` }}
                />
              </div>
              {i > 0 && (
                <p className="text-[11px] text-body dark:text-gray-500 mt-0.5">
                  −{formatNumber(stage.dropOffUsers)} users · median {median}
                </p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function V2ActivationCard({
  activation,
  journey,
}: {
  activation: ActivationV2
  journey: JourneySplitV2
}) {
  const pct = (n: number, d: number) => (d > 0 ? `${(n / d) * 100}%` : 'n/a')
  return (
    <Card title="Activation & journey">
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Activated users</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(activation.activatedUsers)}</dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Qualified (second msg)</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(activation.qualifiedActivatedUsers)} <span className="text-xs text-body dark:text-gray-500">({pct(activation.qualifiedActivatedUsers, activation.activatedUsers)})</span></dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Activation rate (of flow)</dt><dd className="font-medium text-heading dark:text-white">{pct(activation.activatedFromFlow, funnelCohortFromActivation(activation))}</dd></div>
        <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
          <p className="text-[11px] text-body dark:text-gray-500 mb-1">By journey</p>
          <div className="flex justify-between text-xs"><span className="text-body dark:text-gray-400">Wallet-activated</span><span className="font-medium text-heading dark:text-white">{formatNumber(journey.wallet.activatedUsers)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-body dark:text-gray-400">Guest-activated</span><span className="font-medium text-heading dark:text-white">{formatNumber(journey.guest.activatedUsers)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-body dark:text-gray-400">Signed-in users</span><span className="font-medium text-heading dark:text-white">{formatNumber(journey.wallet.authSessionUsers)}</span></div>
        </div>
      </dl>
    </Card>
  )
}

function funnelCohortFromActivation(a: ActivationV2): number {
  // We don't surface cohort size here; compute activation rate denominator from flow
  // by re-deriving: activatedFromFlow/activationRate when available.
  return a.activationRate > 0 ? Math.round(a.activatedFromFlow / a.activationRate) : 0
}

function V2AIReliabilityCard({ reliability }: { reliability: AIReliabilityV2 }) {
  const fmtMs = (v: number | null) => (v == null ? '—' : `${v}ms`)
  return (
    <Card title="AI reliability">
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Requests</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(reliability.requests)}</dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Success rate</dt><dd className="font-medium text-heading dark:text-white">{formatPct(reliability.successRate, 1)}</dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Failed / timeout</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(reliability.failed)} / {formatNumber(reliability.timeout)}</dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Rate-limited</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(reliability.rateLimitEvents)}</dd></div>
        <div className="flex justify-between text-xs"><dt className="text-body dark:text-gray-400">Latency p50 / p90 / p95</dt><dd className="font-medium text-heading dark:text-white">{fmtMs(reliability.latencyMs.p50)} / {fmtMs(reliability.latencyMs.p90)} / {fmtMs(reliability.latencyMs.p95)}</dd></div>
      </dl>
      {Object.keys(reliability.byErrorCategory).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {Object.entries(reliability.byErrorCategory).map(([cat, n]) => (
            <span key={cat} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-body dark:text-gray-400">
              {cat}: {n}
            </span>
          ))}
        </div>
      )}
    </Card>
  )
}

function V2RetentionCard({ cohorts }: { cohorts: RetentionCohortV2[] }) {
  const fmt = (r: { value: number | null; preliminary: boolean } | undefined) => {
    if (!r || r.value == null) return <span className="text-body dark:text-gray-500">—</span>
    return (
      <span>
        {formatPct(r.value, 0)}
        {r.preliminary && <span className="ml-1 text-[10px] text-amber-500">~</span>}
      </span>
    )
  }
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-heading dark:text-white mb-1">Retention by activation cohort</h2>
      <p className="text-[11px] text-body dark:text-gray-500 mb-4">Exact-day D1/D7/D30 · ~ = preliminary (observation window not elapsed)</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-body dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th className="py-1.5 pr-3 font-medium">Cohort week</th>
              <th className="py-1.5 pr-3 font-medium text-right">Activated</th>
              <th className="py-1.5 pr-3 font-medium text-right">D1</th>
              <th className="py-1.5 pr-3 font-medium text-right">D7</th>
              <th className="py-1.5 pr-3 font-medium text-right">D30</th>
              <th className="py-1.5 font-medium text-right">Rolling D7</th>
            </tr>
          </thead>
          <tbody>
            {cohorts.map((c) => (
              <tr key={c.activationWeek} className="border-b border-gray-50 dark:border-gray-800">
                <td className="py-2 pr-3 text-body dark:text-gray-400">{c.activationWeek}</td>
                <td className="py-2 pr-3 text-right font-medium text-heading dark:text-white">{formatNumber(c.activatedUsers)}</td>
                <td className="py-2 pr-3 text-right text-heading dark:text-white">{fmt(c.d1)}</td>
                <td className="py-2 pr-3 text-right text-heading dark:text-white">{fmt(c.d7)}</td>
                <td className="py-2 pr-3 text-right text-heading dark:text-white">{fmt(c.d30)}</td>
                <td className="py-2 text-right text-heading dark:text-white">{(c.d7Rolling != null && c.d7Exact != null) ? `${formatPct(c.d7Rolling, 0)}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function V2AcquisitionCard({ rows }: { rows: AcquisitionRowV2[] }) {
  const total = rows.reduce((s, r) => s + r.visitors, 0)
  return (
    <Card title="Acquisition by source">
      {rows.length === 0 ? (
        <p className="text-xs text-body dark:text-gray-500">No session-start events in period.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.source} className="flex items-center gap-2 text-sm">
              <span className="text-heading dark:text-white capitalize w-20">{r.source}</span>
              <span className="text-body dark:text-gray-400">{formatNumber(r.visitors)}</span>
              <span className="ml-auto text-xs text-body dark:text-gray-500">
                {formatPct(r.activationRate, 0)} activate
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-body dark:text-gray-500 mt-4">{formatNumber(total)} visitors · activation rate per source</p>
    </Card>
  )
}

function V2IdentityCard({ identity, engagement }: { identity: IdentityAdoptionV2; engagement: EngagementV2 }) {
  return (
    <Card title="Identity & privacy adoption">
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">DID creations</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(identity.didCreation.succeeded)} <span className="text-xs text-body dark:text-gray-500">({formatPct(identity.didCreation.successRate, 0)})</span></dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">History saves</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(identity.historySave.succeeded)} <span className="text-xs text-body dark:text-gray-500">({formatPct(identity.historySave.successRate, 0)})</span></dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">History retrievals</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(identity.historyRetrieval.succeeded)} <span className="text-xs text-body dark:text-gray-500">({formatPct(identity.historyRetrieval.successRate, 0)})</span></dd></div>
        <div className="pt-2 border-t border-gray-100 dark:border-gray-700 text-xs">
          <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Messages sent</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(engagement.messages)}</dd></div>
          <div className="flex justify-between"><dt className="text-body dark:text-gray-400">AI responses</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(engagement.aiResponses)}</dd></div>
          <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Second messages</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(engagement.secondMessages)}</dd></div>
        </div>
      </dl>
    </Card>
  )
}

function V2DataQualityCard({ dq }: { dq: DataQualityV2 }) {
  const coverage = Object.values(dq.funnelCoverage).some((n) => n > 0)
  return (
    <Card title="Data quality">
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Events stored</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(dq.acceptedToday)}</dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Duplicates dropped</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(dq.duplicateToday)}</dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Rejected / invalid</dt><dd className="font-medium text-heading dark:text-white">{formatNumber(dq.rejectedToday)}</dd></div>
        <div className="flex justify-between"><dt className="text-body dark:text-gray-400">Unknown attribution</dt><dd className="font-medium text-heading dark:text-white">{formatPct(dq.unknownSourceRate, 1)}</dd></div>
        <div className="flex justify-between text-xs"><dt className="text-body dark:text-gray-400">Funnel coverage</dt><dd className={coverage ? 'font-medium text-green-600' : 'font-medium text-amber-500'}>{coverage ? 'tracking live' : 'no v2 events yet'}</dd></div>
      </dl>
      <p className="text-[11px] text-body dark:text-gray-500 mt-3">
        Zeroes here mean “no data yet”, not “no users”. Empty v2 events → tracking not deployed,
        which is distinct from a real zero.
      </p>
    </Card>
  )
}
